import React from 'react';
import { auswertungenRestText, erreichtProzent } from './abschlussLogic';
import type { TestErgebnisAnzeige } from './teilnahmeSeiteLogic';
import { HINWEISKASTEN, KNOPF_PRIMAER } from './teilnahmeSeiteStil';

/**
 * Letzte Karte des Testlaufs: wie im Echtbetrieb nur Punkte, Grenze und
 * bestanden oder nicht, dazu das Rest-Kontingent des Links. Keine Liste
 * falscher Fragen — ein offener Test-Link waere sonst ein Antwort-Orakel;
 * die richtigen Antworten stehen im Editor. Bewusst ohne Zertifikat,
 * Download und E-Mail (es gibt keine Teilnahme).
 */
export const TestErgebnisAnsicht: React.FC<{
  auswertung: TestErgebnisAnzeige;
  onNeuStarten: () => void;
}> = ({ auswertung, onNeuStarten }) => {
  const { punkte, punkteMax, bestanden, bestehenProzent } = auswertung;
  const prozent = erreichtProzent(punkte, punkteMax);
  const rest = auswertungenRestText(auswertung.auswertungenRest);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className={`flex items-center justify-center w-12 h-12 rounded-full shrink-0 ${
            bestanden ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          }`}
        >
          <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            {bestanden ? <path d="M5 13l4 4L19 7" /> : <path d="M6 6l12 12M18 6L6 18" />}
          </svg>
        </span>
        <div>
          <p className="text-sm font-medium text-slate-500">Ergebnis des Testlaufs</p>
          <h2 className={`text-2xl font-bold ${bestanden ? 'text-emerald-700' : 'text-red-700'}`}>
            {bestanden ? 'Bestanden' : 'Nicht bestanden'}
          </h2>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-3">
        <div className="p-3 bg-slate-50 rounded-xl">
          <dt className="text-sm text-slate-500">Punkte</dt>
          <dd className="font-semibold">
            {punkte} von {punkteMax}
            {punkteMax > 0 && <span className="text-slate-500 font-normal"> ({prozent} %)</span>}
          </dd>
        </div>
        <div className="p-3 bg-slate-50 rounded-xl">
          <dt className="text-sm text-slate-500">Zum Bestehen nötig</dt>
          <dd className="font-semibold">{bestehenProzent} %</dd>
        </div>
      </dl>

      {punkteMax === 0 && <p className="text-slate-700">Diese Unterweisung hat keine Verständnisfragen.</p>}

      <p className="text-slate-600">Welche Antworten richtig sind, zeigt der Editor.</p>

      <div className={HINWEISKASTEN}>
        Testlauf: Es wurde nichts gespeichert — keine Teilnahme, kein Zertifikat, keine E-Mail.
        {rest && <span className="block mt-1 font-semibold">{rest}</span>}
      </div>

      <button type="button" onClick={onNeuStarten} className={KNOPF_PRIMAER}>
        Test neu starten
      </button>
    </div>
  );
};

/**
 * Statt eines Ergebnisses: das Auswertungs-Kontingent des Test-Links ist
 * aufgebraucht (Schutz der anonymen RPC gegen Dauerabfragen). Der Neustart
 * bleibt — durchklicken geht weiter, ausgewertet wird erst mit neuem Link.
 */
export const TestKontingentAnsicht: React.FC<{ onNeuStarten: () => void }> = ({ onNeuStarten }) => (
  <div className="space-y-5">
    <div className="space-y-1">
      <p className="text-sm font-medium text-slate-500">Ergebnis des Testlaufs</p>
      <h2 className="text-2xl font-bold text-slate-900">Keine Auswertung mehr möglich</h2>
    </div>
    <div className={HINWEISKASTEN} role="status">
      Mit diesem Test-Link wurden schon so viele Testläufe ausgewertet, wie erlaubt sind. Erzeuge im Editor mit
      „Testen" einen neuen Test-Link, um weitere Durchläufe auszuwerten.
    </div>
    <button type="button" onClick={onNeuStarten} className={KNOPF_PRIMAER}>
      Test neu starten
    </button>
  </div>
);
